/* eslint-disable import/extensions */
import {type IProject} from '@unbrs/plastic-data/src/brs';
import {type IRegionUN} from '@unbrs/plastic-data/src/types/project';
import {BrsBaseWidget} from '../base';
import html from './views/projects-statistics.tmpl.html';

export type BrsPlasticStatisticsWidgetOptions = {
	dataSource?: kendo.data.DataSource;
};

export class BrsPlasticStatisticsWidget extends BrsBaseWidget {
	public override options: Record<string, unknown> = {
		name: 'brsplasticstatisticswidget',
	};

	private dataSource?: kendo.data.DataSource;
	private readonly chart?: kendo.dataviz.ui.Chart;
	private readonly chartCountries?: kendo.dataviz.ui.Chart;
	constructor(selector: Element, options: BrsPlasticStatisticsWidgetOptions) {
		super(selector, options);

		if (options?.dataSource) {
			this.setDataSource(options.dataSource);
		}

		this.element.html(html);

		this.chart = $('.brs-projects-statics-regionschart', this.element).kendoChart({
			title: {
				text: 'Projects per region',
			},
			legend: {
				position: 'bottom',
			},
			tooltip: {
				visible: true,
				template: '#= category# #= kendo.format("{0:P}", percentage)#',
			},
			seriesDefaults: {
				labels: {
					visible: true,
					format: '{0}%',
					template: '#= category #: #= value# projects (#= kendo.format("{0:P}", percentage)#) ',
				},
			},
			series: [{
				name: 'pie',
				type: 'pie',
			}],
		}).data('kendoChart');

		this.chartCountries = $('.brs-projects-statics-regionscountrieschart', this.element).kendoChart({
			title: {
				text: 'Countries with projects per region',
			},
			legend: {
				position: 'bottom',
			},
			tooltip: {
				visible: true,
				template: '#= category# #= kendo.format("{0:P}", percentage)#',
			},
			seriesDefaults: {
				labels: {
					visible: true,
					format: '{0}%',
					template: '#= category #: #= value# countries (#= kendo.format("{0:P}", percentage)#) ',
				},
			},
			series: [{
				name: 'pie',
				type: 'pie',
			}],
		}).data('kendoChart');
	}

	refresh() {
		if (this.chart === undefined) {
			return;
		}

		const pieSeries = this.chart.findSeriesByName('pie');
		if (pieSeries === undefined) {
			return;
		}

		pieSeries.data(this.getRegionsPieChartSeries());
		this.chart.refresh();

		if (this.chartCountries === undefined) {
			return;
		}

		const pieSeriesCountries = this.chartCountries.findSeriesByName('pie');
		if (pieSeriesCountries === undefined) {
			return;
		}

		pieSeriesCountries.data(this.getCountriesPerRegionPieChartSeries());
		this.chartCountries.refresh();
	}

	getRegionsPieChartSeries() {
		if (this.dataSource === undefined) {
			return;
		}

		const counter = new Map<IRegionUN, number>();

		// eslint-disable-next-line unicorn/no-array-for-each
		this.dataSource.view().forEach((item: any) => {
			const project = item as IProject;
			const projectRegions = new Set<IRegionUN>();
			for (const region of project.unRegions ?? []) {
				projectRegions.add(region);
			}

			for (const country of project.beneficiaries) {
				projectRegions.add(country.countryRegionUn);
			}

			for (const region of projectRegions) {
				counter.set(region, (counter.get(region) ?? 0) + 1);
			}
		});
		const result: Array<{category: string; value: number}> = [];
		for (const unRegion of counter.keys()) {
			result.push({
				category: unRegion,
				value: counter.get(unRegion) ?? 0,
			});
		}

		return result.sort((a, b) => b.value - a.value);
	}

	getCountriesPerRegionPieChartSeries() {
		if (this.dataSource === undefined) {
			return;
		}

		const counter = new Map<IRegionUN, Set<string>>();

		// eslint-disable-next-line unicorn/no-array-for-each
		this.dataSource.view().forEach((item: any) => {
			const project = item as IProject;

			for (const country of project.beneficiaries) {
				if (!counter.has(country.countryRegionUn)) {
					counter.set(country.countryRegionUn, new Set<string>());
				}

				counter.get(country.countryRegionUn)?.add(country.iso2);
			}
		});

		const result: Array<{category: string; value: number}> = [];
		for (const unRegion of counter.keys()) {
			result.push({
				category: unRegion,
				value: counter.get(unRegion)?.size ?? 0,
			});
		}

		return result.sort((a, b) => b.value - a.value);
	}

	setDataSource(dataSource: kendo.data.DataSource) {
		this.dataSource = dataSource;
		this.dataSource.bind('change', this.refresh.bind(this));
	}
}

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface JQuery {
		kendoBrsPlasticStatisticsWidget(options?: BrsPlasticStatisticsWidgetOptions): JQuery;
		data(widgetName: 'kendoBrsPlasticStatisticsWidget'): BrsPlasticStatisticsWidget;
	}
}

BrsBaseWidget.registerBrsWidget(
	BrsPlasticStatisticsWidget,
	'BrsPlasticStatisticsWidget',
);
